/**
 * RegionDetailsPanel — in-game map detail overlay.
 *
 * Replaces the action sidebar while the 🗺 chip is on. Region outlines
 * and bonus badges are always rendered on the map (combined view —
 * there is no Territories/Regions mode toggle). Map tap behaviour:
 *
 *   • Tap a territory on the map → selects it; the panel shows its
 *     name, parent region (clickable to focus that region instead),
 *     bonus, and adjacency list.
 *   • Tap a region row in the directory → focuses that region on the
 *     map (paints its bonus badge and clears any selected territory).
 *
 * Renders inside the same layout slot the phase action panels occupy
 * (portrait bottom reserve or landscape left sidebar, sized by
 * useMapDimensions.actionPanelWidth).
 */

import React from 'react'
import { ScrollView } from 'react-native'
import type { GameState } from '../../lib/gameState'
import type { GamePlayerResponse } from '@warcarp/api-types'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { RegionAccordion } from '../map/RegionAccordion'

export interface RegionDetailsPanelProps {
  state: GameState
  gamePlayers: GamePlayerResponse[]
  /** regionId → hex colour (the same map MapView uses for region borders). */
  regionColors: Record<string, string>
  /** Toggle a region's expanded state (and focus it on the map). */
  onRegionPress?: (regionId: string) => void
  selectedRegionId?: string | null
  /** Territory selected by a map tap or a list tap. */
  selectedTerritoryId?: string | null
  /** Select/deselect a territory from within its region body. */
  onSelectTerritory?: (territoryId: string) => void
  /** Bump to scroll the list back to the top (wired to the map's Reset). */
  resetSignal?: number
}

export function RegionDetailsPanel({
  state,
  gamePlayers: _gamePlayers,
  regionColors,
  onRegionPress,
  selectedRegionId,
  selectedTerritoryId,
  onSelectTerritory,
  resetSignal,
}: RegionDetailsPanelProps) {
  const { isLandscape, actionPanelWidth } = useMapDimensions(state)

  // Own the scroll container so RegionAccordion can centre the expanded region
  // (scroll-to-centre) and the map Reset can send the list back to the top.
  const scrollRef = React.useRef<ScrollView>(null)
  const viewportH = React.useRef(0)
  React.useEffect(() => {
    if (resetSignal === undefined) return
    scrollRef.current?.scrollTo({ y: 0, animated: true })
  }, [resetSignal])

  return (
    <ScrollView
      ref={scrollRef}
      onLayout={(e) => {
        viewportH.current = e.nativeEvent.layout.height
      }}
      className="flex-1"
      contentContainerStyle={{ padding: 8 }}
      style={isLandscape ? { maxWidth: actionPanelWidth } : undefined}
      accessibilityLabel="Region details panel"
    >
      <RegionAccordion
        regions={state.map.regions}
        territories={state.map.territories}
        regionColors={regionColors}
        expandedRegionId={selectedRegionId ?? null}
        selectedTerritoryId={selectedTerritoryId ?? null}
        onToggleRegion={(id) => onRegionPress?.(id)}
        onSelectTerritory={(id) => onSelectTerritory?.(id)}
        scrollParentRef={scrollRef}
        getViewportHeight={() => viewportH.current}
      />
    </ScrollView>
  )
}
